import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogActionsProps,
  DialogContent,
  DialogContentProps,
  DialogProps,
  DialogTitle,
  DialogTitleProps,
  Divider,
  IconButton,
  Typography
} from '@mui/material';
import Spinner from 'components/ui/data-display/Spinner';
import React from 'react';

/**
 * Props for the highly reusable and customizable EnhancedDialog component.
 * Integrates directly with MUI structural APIs to ensure native behavior and flexibility.
 */
export interface EnhancedDialogProps extends Omit<DialogProps, 'title'> {
  /** Controls the visibility state of the modal container. Inherited from MUI Dialog. */
  open: boolean;
  /** Triggered upon pressing the cancel button, close icon, or clicking the backdrop. */
  onClose: () => void;
  /** Callback fired when the user submits the form or clicks the main confirmation action. */
  onSubmit?: any | (() => void | Promise<void>);
  /** Main dynamic text title displayed in the modal header. */
  title: React.ReactNode;
  /** Optional smaller text placed directly above the main title to define operations context. */
  subTitle?: React.ReactNode;
  /** Label text inside the primary confirmation action button. Defaults to 'Salva Modifiche'. */
  submitLabel?: string;
  /** Label text inside the secondary dismissal button. Defaults to 'Annulla'. */
  cancelLabel?: string;
  /** If true, activates Loading States: shows spinner, disables all buttons and backdrop clicks. */
  isSubmitting?: boolean;
  /** Evaluates client-side validation logic. If false, securely locks the primary action button. */
  isValid?: boolean;
  /** Optional layout flag to strip the standard top and bottom dividers for a cleaner visual deck. */
  hideDividers?: boolean;
  /** Custom underlying props directly injected into the MUI DialogTitle container. */
  titleProps?: Partial<DialogTitleProps>;
  /** Custom underlying props directly injected into the MUI DialogContent container. */
  contentProps?: Partial<DialogContentProps>;
  /** Custom underlying props directly injected into the MUI DialogActions container. */
  actionsProps?: Partial<DialogActionsProps>;
  /** Configuration overrides for the primary submission button. Merges with default contained styles. */
  submitButtonProps?: Partial<ButtonProps>;
  /** Configuration overrides for the secondary dismissal button. Merges with default outlined styles. */
  cancelButtonProps?: Partial<ButtonProps>;
  /** Core layout body injected inside the scrollable content pane (e.g., custom form fields). */
  children?: React.ReactNode;
  costumFooter?: React.ReactNode;
}

export const EnhancedDialog = ({
  open,
  onClose,
  onSubmit,
  title,
  subTitle,
  submitLabel = 'Salva Modifiche',
  cancelLabel = 'Annulla',
  isSubmitting = false,
  isValid = true,
  hideDividers = false,
  maxWidth = 'sm',
  titleProps,
  contentProps,
  actionsProps,
  submitButtonProps,
  cancelButtonProps,
  children,
  costumFooter,
  ...muiDialogProps // Spreads all other native MUI Dialog props (fullScreen, transition, slots, etc.)
}: EnhancedDialogProps) => {
  const paperProps = muiDialogProps.PaperProps;

  const handlePaperClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
    event.stopPropagation();
    paperProps?.onClick?.(event);
  };

  const handlePaperMouseDown: React.MouseEventHandler<HTMLDivElement> = (event) => {
    event.stopPropagation();
    paperProps?.onMouseDown?.(event);
  };

  const handlePaperKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key !== 'Escape') {
      event.stopPropagation();
    }
    paperProps?.onKeyDown?.(event);
  };

  const handlePaperKeyUp: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key !== 'Escape') {
      event.stopPropagation();
    }
    paperProps?.onKeyUp?.(event);
  };

  //  Make sure your local onClose prop matches the basic parameterless close function
  // or the exact signature used by the frontend layout.
  const handleSafeClose = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    event: React.SyntheticEvent | object,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    // Hardened UX: Prevent the technician from closing the form halfway through an API transaction
    if (isSubmitting) return;

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleSafeClose}
      maxWidth={maxWidth}
      fullWidth
      scroll="paper"
      aria-labelledby="generic-dialog-title"
      PaperProps={{
        ...paperProps,
        onClick: handlePaperClick,
        onMouseDown: handlePaperMouseDown,
        onKeyDown: handlePaperKeyDown,
        onKeyUp: handlePaperKeyUp
      }}
      {...muiDialogProps}>
      {/* HEADER CONTAINER */}
      <DialogTitle
        id="generic-dialog-title"
        {...titleProps}
        sx={{
          m: 0,
          p: 2.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          ...titleProps?.sx
        }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
          <Typography
            component="h5"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              fontSize: '1.25rem',
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}>
            {title}
          </Typography>

          {subTitle && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
                lineHeight: 1.4,
                letterSpacing: '0.01em'
              }}>
              {subTitle}
            </Typography>
          )}
        </Box>

        <IconButton
          onClick={onClose}
          size="small"
          disabled={isSubmitting}
          aria-label="close"
          sx={{
            color: 'text.disabled',
            bgcolor: 'background.default',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            transition: 'all 0.2s ease',
            flexShrink: 0,
            '&:hover': { color: 'text.secondary', bgcolor: '#f1f5f9', borderColor: '#cbd5e1' }
          }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {!hideDividers && <Divider />}

      {/* CORE CONTENT CONTAINER */}
      <DialogContent {...contentProps} sx={{ ...contentProps?.sx }}>
        {children}
      </DialogContent>

      {!hideDividers && <Divider />}

      {/* FOOTER ACTIONS CONTAINER */}
      <DialogActions {...actionsProps} sx={{ ...actionsProps?.sx }}>
        {costumFooter ? (
          costumFooter
        ) : (
          <>
            <Button
              onClick={onClose}
              disabled={isSubmitting}
              variant="outlined"
              {...cancelButtonProps}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                ...cancelButtonProps?.sx
              }}>
              {cancelLabel}
            </Button>

            <Button
              onClick={submitButtonProps?.type === 'submit' ? undefined : onSubmit}
              disabled={!isValid || isSubmitting}
              variant="contained"
              startIcon={isSubmitting ? <Spinner /> : submitButtonProps?.startIcon}
              {...submitButtonProps}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                minWidth: '110px',
                '&.Mui-disabled': {
                  bgcolor: isSubmitting ? 'primary.main' : undefined,
                  opacity: isSubmitting ? 0.7 : undefined
                },
                ...submitButtonProps?.sx
              }}>
              {submitLabel}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
